import axios from 'axios'
import { localStorage as local } from '@entrata/utils'
import {
  JWT_STORAGE_KEY,
  CUSTOM_JWT_STORAGE_KEY,
} from 'portal-modules/common/constants/keys'
import getClusterEnv from 'portal-modules/common/utils/get-cluster-env'
import processPostData from 'portal-modules/common/utils/process-post-data'
import { getSessionCookies } from 'portal-utils/session-cookies'

const boolIsProductionEnv =
  window.rpSettings && window.rpSettings.isProductionEnv
    ? window.rpSettings.isProductionEnv
    : process.env.NODE_ENV === 'production' &&
      window.location.hostname === 'tenants.entrata.com'

const domain =
  process.env.NODE_ENV === 'production' || process.env.NODE_BRANCH === 'local'
    ? window.location.host
        .replace('entrata', 'residentportal')
        .replace('tenants.', '')
    : `${process.env.DNS_HANDLE || 'd05d0001'}.residentportaldev.com`

let ApiDomain = `.${domain}/api`
if (window.rpSettings.rpApiSuffix && window.rpSettings.rpApiSuffix != '') {
  ApiDomain = `${window.rpSettings.rpApiSuffix}/api`
}

const apiProtocol = !window.location.origin.includes('http://localhost')
  ? window.location.protocol
  : 'https:'
const getBaseUrl = () =>
  (process.env.NODE_ENV === 'production'
    ? `${window.location.protocol}//${getClusterEnv()}`
    : `${apiProtocol}//${getClusterEnv()}`) + ApiDomain

const strHaReplace = 'ha.'
const AUTH_URL =
  (process.env.NODE_ENV === 'production' || process.env.NODE_BRANCH === 'local'
    ? `${window.location.protocol}//${window.location.host.replace(
        'tenants.',
        strHaReplace,
      )}`
    : 'https://ha.' +
      (boolIsProductionEnv ? 'entrata' : 'd05d0001.entratadev') +
      '.com') + '/RpApi'

const HA_ACTIONS = [
  'login',
  'resetPassword',
  'validateResetPasswordCode',
  'updatePassword',
]

export const getUrl = ({ controller, action, forceBaseUrl = 0 }, params = {}) =>
  `${
    HA_ACTIONS.includes(action) && forceBaseUrl == 0 ? AUTH_URL : getBaseUrl()
  }/?controller=${controller}&action=${action}${Object.entries(params).reduce(
    (str, [key, value]) =>
      `${str}&${window.encodeURI(key)}=${window.encodeURI(value)}`,
    '',
  )}`

// api utils
/**
 * Extracts api data from axios response object
 * @param {object} response Axios response object
 */
const getResponseData = response => response.data

export const getHeaders = (skipAuth, useCustomToken = false) => ({
  'Content-Type': 'application/json',
  'X-Consumer': 'tpweb',
  'X-Consumer-Version': '1.0',
  ...(skipAuth
    ? {}
    : {
        Authorization: `Bearer ${
          useCustomToken
            ? local.get(CUSTOM_JWT_STORAGE_KEY)
            : getSessionCookies(JWT_STORAGE_KEY)
        }`,
      }),
})

const getMultipartHeaders = () => ({
  'Content-Type': 'multipart/form-data',
  'X-Consumer': 'tpweb',
  'X-Consumer-Version': '1.0',
  Authorization: `Bearer ${getSessionCookies(JWT_STORAGE_KEY)}`,
})

/**
 * Creates an api method and pipes results through `getResponseData`
 * and errors through `handleRequestError`.
 * @param {object} config
 */
export const createMethod = config => axios(config).then(getResponseData)

export const get =
  ({ skipAuth, ...urlProps }) =>
  (params, config = {}) =>
    createMethod({
      method: 'get',
      url: getUrl(urlProps, params),
      headers: getHeaders(skipAuth),
      ...config,
    })

export const post =
  urlProps =>
  (data, config = {}) =>
    createMethod({
      method: 'post',
      url: getUrl(urlProps, processPostData({})),
      data,
      headers: getHeaders(),
      ...config,
    })

export const postMultipart =
  urlProps =>
  (data, config = {}) =>
    createMethod({
      method: 'post',
      url: getUrl(urlProps, processPostData({})),
      data,
      headers: getMultipartHeaders(),
      ...config,
    })

export const postWithToken =
  ({ skipAuth = false, useCustomToken = false, ...urlProps }) =>
  (data, config = {}) =>
    createMethod({
      method: 'post',
      url: getUrl(urlProps),
      data,
      headers: getHeaders(skipAuth, useCustomToken),
      ...config,
    })
